import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import { IoWalletSharp } from "react-icons/io5";
import { HiDotsVertical } from "react-icons/hi";
import { TbZoomMoney } from "react-icons/tb";
import { GiWallet } from "react-icons/gi";
import updatestocksApi from '../data/stockhistory';

import { Pie, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement
} from 'chart.js';

import parties from '../data/party';
import stocksApi from '../data/stock';

function findLatestStockHistory(
    updateStockRecords: any[],
    stock_id: number,
    cat_id?: number
) {
    const filtered = updateStockRecords.filter(
        rec =>
            rec.stock_id === stock_id &&
            String(rec.cat_id ?? "") === String(cat_id ?? "")
    );
    if (filtered.length === 0) return null;
    return filtered.reduce((latest, current) => {
        const latestTime = latest.current_price_date
            ? new Date(latest.current_price_date).getTime()
            : 0;
        const currentTime = current.current_price_date
            ? new Date(current.current_price_date).getTime()
            : 0;
        if (currentTime > latestTime) return current;
        if (currentTime === latestTime) {
            return (current.id ?? 0) > (latest.id ?? 0) ? current : latest;
        }
        return latest;
    }, filtered[0]);
}
async function fetchSettings() {
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/setting`);
        if (!res.ok) return [];
        const data = await res.json();
        return data;
    } catch (err) {
        return [];
    }
}

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement);

interface Party {
    id: number;
    date: string;
    name: string;
    number: string;
    contact_person_name: string;
    contact_person_number: string;
    categories: string[];
    deleteparty: number;
    amount?: number;
    percentage?: number;
    total_amount?: number;
}

interface StockCategory {
    id: number;
    stock_name: string;
    unit: string;
    total_amount: string | number;
    indirect_charges: string | number;
    perUnitCharge: string | number;
    category: string;
}
interface Stock {
    id: number | string;
    date?: string;
    party_id: number;
    total_amount: number | string;
    indirect_charges?: number | string;
    categories: StockCategory[];
    name?: string;
    date_of_purchase?: string;
    unit?: string;
    price_per_unit?: string | number;
}

const growthOptions = {
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: {
        x: { grid: { display: false }, ticks: { color: '#b1b5c4' } },
        y: { grid: { color: "#f3f3f3" }, ticks: { color: '#b1b5c4' } }
    },
    maintainAspectRatio: false,
    elements: { point: { radius: 5 } }
};

const pieOptions = { plugins: { legend: { display: false } } };

const analyticsOptions = {
    plugins: {
        legend: { display: true, position: "top", labels: { color: "#222" } }
    },
    scales: {
        x: { grid: { display: false }, ticks: { color: '#b1b5c4' } },
        y: { grid: { color: "#f3f3f3" }, ticks: { color: '#b1b5c4' } }
    },
    maintainAspectRatio: false,
    elements: { point: { radius: 0 } }
};

const PERIOD_OPTIONS = [
    { key: "All", label: "All" },
    { key: "today", label: "Today" },
    { key: "thisWeek", label: "This Week" },
    { key: "thisMonth", label: "This Month" }
];

function allStocksMinTransactionDateFromStocks(stocks: Stock[]): Date | undefined {
    if (!stocks || stocks.length === 0) return undefined;
    const dates = stocks.map(s => s.date_of_purchase || s.date).map(d => (d ? new Date(d) : undefined)).filter(Boolean) as Date[];
    if (!dates.length) return undefined;
    return dates.reduce((min, d) => d < min ? d : min, dates[0]);
}
function allStocksMaxTransactionDateFromStocks(stocks: Stock[]): Date | undefined {
    if (!stocks || stocks.length === 0) return undefined;
    const dates = stocks.map(s => s.date_of_purchase || s.date).map(d => (d ? new Date(d) : undefined)).filter(Boolean) as Date[];
    if (!dates.length) return undefined;
    return dates.reduce((max, d) => d > max ? d : max, dates[0]);
}

function getPeriodRange(period: string): [Date | null, Date | null] {
    const now = new Date();
    let from: Date | null = null, to: Date | null = null;
    switch (period) {
        case "today":
            from = new Date(now); from.setHours(0, 0, 0, 0);
            to = new Date(now); to.setHours(23, 59, 59, 999);
            break;
        case "thisWeek":
            from = new Date(now); from.setDate(now.getDate() - now.getDay());
            from.setHours(0, 0, 0, 0);
            to = new Date(now); to.setHours(23, 59, 59, 999);
            break;
        case "thisMonth":
            from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
            to = new Date(now); to.setHours(23, 59, 59, 999);
            break;
        case "All":
        default:
            from = null; to = null;
    }
    return [from, to];
}

function getAllUniqueCategories(stocks: Stock[]): string[] {
    const set = new Set<string>();
    for (const stock of stocks) {
        const cats = Array.isArray(stock.categories) ? stock.categories : [];
        cats.forEach(cat => {
            if (cat && typeof cat === "object" && typeof cat.category === "string" && cat.category.trim()) {
                set.add(cat.category);
            }
        });
    }
    return Array.from(set);
}

// --- Add helper function: Calculate invested, return, percent for a party ---
function getPartyInvestmentAndReturn(party: Party, allStocks: Stock[], updatestocks: any[]): { invested: number; returned: number; percent: number } {
    // Find all stocks belonging to this party
    const stocksForParty = allStocks.filter(stock => stock.party_id === party.id);
    let invested = 0;
    let returned = 0;
    for (const stock of stocksForParty) {
        const categories = Array.isArray(stock.categories) ? stock.categories : [];
        for (const cat of categories) {
            // Invested: sum all total_amount
            let amt = cat.total_amount;
            if (typeof amt === "string") amt = parseFloat(amt) || 0;
            invested += amt || 0;

            // Returned: try to get latest current price for this stock/category
            if (updatestocks && Array.isArray(updatestocks)) {
                const stockId = Number(stock.id);
                const catId = Number(cat.id);
                const latest = findLatestStockHistory(updatestocks, stockId, catId);
                if (latest && latest.current_price) {
                    let curPrice = latest.current_price;
                    if (typeof curPrice === "string") curPrice = parseFloat(curPrice) || 0;
                    returned += curPrice || 0;
                }
            }
        }
    }
    const percent = invested > 0 ? ((returned - invested) / invested) * 100 : 0;
    return { invested, returned, percent };
}

const PartyComparisonSummary: React.FC<{
    party1Stocks: Stock[];
    party2Stocks: Stock[];
    party1Name: string;
    party2Name: string;
}> = ({ party1Stocks, party2Stocks, party1Name, party2Name }) => {
    const calculateTotal = (stocks: Stock[]): number => {
        return stocks.reduce((sum, stock) => {
            const cats = Array.isArray(stock.categories) ? stock.categories : [];
            return sum + cats.reduce((catSum: number, cat: any) => {
                let amt = typeof cat.total_amount === 'string'
                    ? parseFloat(cat.total_amount) || 0
                    : cat.total_amount || 0;
                return catSum + amt;
            }, 0);
        }, 0);
    };
    const party1Total = calculateTotal(party1Stocks);
    const party2Total = calculateTotal(party2Stocks);
    const difference = Math.abs(party1Total - party2Total);
    const betterParty = party1Total > party2Total ? party1Name : party2Name;
    const isParty1Better = party1Total > party2Total;
    return (
        <div className="bg-[#F1FFF6] rounded-lg p-3 mt-2">
            <div className="text-xs text-[#7B849B] mb-2">Comparison Summary</div>
            <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center">
                    <span className="text-sm text-[#222] font-medium">{party1Name}:</span>
                    <span className="text-sm text-[#222] font-semibold">₹{party1Total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center">
                    <span className="text-sm text-[#222] font-medium">{party2Name}:</span>
                    <span className="text-sm text-[#222] font-semibold">₹{party2Total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="border-t border-[#E6E9F0] pt-2 mt-1">
                    <div className="flex justify-between items-center">
                        <span className="text-xs text-[#7B849B]">Better Performance:</span>
                        <span className={`text-sm font-semibold ${isParty1Better ? 'text-[#6D6AFF]' : 'text-[#F8C615]'}`}>
                            {betterParty} (+₹{difference.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

function filterStocksByCategoryAndPeriod(stocks: Stock[], category: string, period: string) {
    const [from, to] = getPeriodRange(period);
    let filtered = stocks;
    if (category && category !== 'All') {
        filtered = filtered.filter(stock => {
            const cats = Array.isArray(stock.categories) ? stock.categories : [];
            return cats.some((cat: any) => typeof cat.category === "string" && cat.category === category);
        });
    }
    if (from && to) {
        filtered = filtered.filter(stock => {
            const stockDateStr = stock.date_of_purchase || stock.date;
            if (!stockDateStr) return false;
            const stockDate = new Date(stockDateStr);
            return stockDate >= from && stockDate <= to;
        });
    }
    return filtered;
}
type HasDate = {
    date?: string;
    date_of_purchase?: string;
};

function filterStocksByDateRange<T extends HasDate>(
    items: T[],
    fromDate: string | null,
    toDate: string | null
): T[] {
    if (!fromDate && !toDate) return items;
    return items.filter(item => {
        const dateStr = item.date_of_purchase || item.date;
        if (!dateStr) return false;
        const itemDate = new Date(dateStr);
        let fromCondition = true, toCondition = true;
        if (fromDate) fromCondition = itemDate >= new Date(fromDate);
        if (toDate) {
            const toD = new Date(toDate);
            toD.setHours(23, 59, 59, 999);
            toCondition = itemDate <= toD;
        }
        return fromCondition && toCondition;
    });
}
function calculateSimpleInterest(principal: number, rate: number, time: number) {
    return principal * rate * time / 100;
}
function calculateCompoundInterest(principal: number, rate: number, time: number) {
    return principal * (Math.pow(1 + rate / 100, time) - 1);
}
// Utility: calculate total invested (sum of all stocks' categories' total_amount)
function calculateTotalInvested(stocks: Stock[]): number {
    let sum = 0;
    for (const stock of stocks) {
        const cats = Array.isArray(stock.categories) ? stock.categories : [];
        for (const cat of cats) {
            let amt = cat.total_amount;
            if (typeof amt === "string") amt = parseFloat(amt) || 0;
            sum += amt || 0;
        }
    }
    return sum;
}

// Utility: calculate opening balance as sum of all stocks' categories' total_amount
// ...but only those purchased up to the selected "from" date (inclusive)
// Calculates the total opening balance of all parties up to and including the selected "from" date.
// Only includes party.total_amount where party.date <= fromDate (if fromDate is given).
function calculateOpeningBalance(
    fromDate: string | null,
    toDate: string | null,
    allParties: any[]
): number {
    // Defensive: If allParties is not an array, return 0
    if (!Array.isArray(allParties)) return 0;
   debugger;
    const getAmount = (val: string | number | undefined): number => {
        if (typeof val === "string") return parseFloat(val) || 0;
        return typeof val === "number" ? val : 0;
    };

    // If no fromDate or fromDate is invalid, sum all total_amount
    if (!fromDate || isNaN(new Date(fromDate).getTime())) {
        return allParties
            .map((p: { total_amount: string | number }) => getAmount(p.total_amount))
            .reduce((sum: number, val: number) => sum + val, 0);
    }

    const from = new Date(fromDate);
    let to: Date | null = null;
    if (toDate && !isNaN(new Date(toDate).getTime())) {
        to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
    }

    // Opening balance includes parties with .date in range [from, to] if given, else from and forward
    let sum = 0;
    for (const party of allParties) {
        if (!party.date) continue;
        const dt = new Date(party.date);
        if (isNaN(dt.getTime())) continue;

        // With from and to range
        if (from && to) {
            if (dt >= from && dt <= to) {
                sum += getAmount(party.total_amount);
            }
        }
        // With only from (no to): inclusively, everything from 'from'
        else if (from && !to) {
            if (dt >= from) {
                sum += getAmount(party.total_amount);
            }
        }
        // With only to (shouldn't occur based on usage, but check for robustness)
        else if (!from && to) {
            if (dt <= to) {
                sum += getAmount(party.total_amount);
            }
        }
    }

    return sum;
}

// Calculate current balance from latest current_price using updatestocksApi
async function calculateCurrentBalanceFromApi(stocks: Stock[]): Promise<number> {
    try {
        const updateStockRecords = await updatestocksApi();
        let sum = 0;
        for (const stock of stocks) {
            const categories = Array.isArray(stock.categories) ? stock.categories : [];
            for (const cat of categories) {
                const stockId = Number(stock.id);
                const catId = Number(cat.id);
                const latest = findLatestStockHistory(updateStockRecords, stockId, catId);
                if (latest && latest.current_price) {
                    let curPrice = latest.current_price;
                    if (typeof curPrice === "string") curPrice = parseFloat(curPrice) || 0;
                    sum += curPrice || 0;
                }
            }
        }
        return sum;
    } catch (err) {
        return 0;
    }
}

function Dashboard() {
    // Data
    const [allParties, setAllParties] = useState<Party[]>([]);
    const [allStocks, setAllStocks] = useState<Stock[]>([]);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [uniqueCategories, setUniqueCategories] = useState<string[]>([]);
    
    const [dateRange, setDateRange] = useState<{ from: string | null, to: string | null }>({ from: null, to: null });
    const [growthPeriod, setGrowthPeriod] = useState<string>('All');
    const [growthCategory, setGrowthCategory] = useState<string>('All');
    const [piePeriod, setPiePeriod] = useState<string>('All');
    const [pieCategory, setPieCategory] = useState<string>('All');

    // Stats
    const [openingBalance, setOpeningBalance] = useState<number>(0); // Dynamic as per from-date
    const [currentBalance, setCurrentBalance] = useState<number>(0);
    const [totalEarning, setTotalEarning] = useState<number>(0);

    // Settings/interest for interest calculation
    const [settings, setSettings] = useState<any[]>([]);

    // Chart/analytics
    const [growthChartData, setGrowthChartData] = useState<any>({
        labels: [],
        datasets: [{
            label: "Working Balance",
            data: [],
            fill: false,
            backgroundColor: "#34d399",
            borderColor: "#34d399",
            borderWidth: 2,
            pointBackgroundColor: "#fff",
            pointBorderColor: "#34d399",
            tension: 0.4
        }]
    });
    const [pieData, setPieData] = useState<any>({
        labels: [],
        datasets: [{
            data: [],
            backgroundColor: ["#1294F2", "#F6830F", "#00CA4E", "#F83C3F", "#FF6B9D", "#9B59B6", "#E67E22", "#3498DB"],
            borderWidth: 0,
        }]
    });
    const [pieCategoryDetails, setPieCategoryDetails] = useState<Array<any>>([]);

    // Analytics
    const [selectedParty1, setSelectedParty1] = useState<string>('');
    const [selectedParty2, setSelectedParty2] = useState<string>('');
    const [party1Stocks, setParty1Stocks] = useState<Stock[]>([]);
    const [party2Stocks, setParty2Stocks] = useState<Stock[]>([]);
    const [analyticsData, setAnalyticsData] = useState({
        labels: [] as string[],
        datasets: [
            {
                label: 'Party 1',
                data: [] as number[],
                borderColor: "#6D6AFF",
                backgroundColor: "rgba(109,106,255,0.07)",
                fill: true,
                tension: 0.4
            },
            {
                label: 'Party 2',
                data: [] as number[],
                borderColor: "#F8C615",
                backgroundColor: "rgba(248,198,21,0.07)",
                fill: true,
                tension: 0.4
            }
        ]
    });
    const [selectedAnalyticsCategory, setSelectedAnalyticsCategory] = useState<string>('All');
    const [allAnalyticsCategories, setAllAnalyticsCategories] = useState<string[]>([]);

    const [useBaseCalculation, setUseBaseCalculation] = useState(false);
    const [interestStartDate, setInterestStartDate] = useState<string>(''); // User can pick from date, default: first transaction
    const [interestEndDate, setInterestEndDate] = useState<string>('');     // User can pick to date, default: last transaction
    const [interestSummary, setInterestSummary] = useState<{
        type: string,
        rate: number,
        calculated: number,
        start: string,
        end: string,
        time: number
    } | null>(null);

    // Store updatestocksApi cache for calculating party sidebar investment/return
    const [updateStockRecordsForParties, setUpdateStockRecordsForParties] = useState<any[]>([]);
    useEffect(() => {
        (async () => {
            setSettings(await fetchSettings());
        })();
        // Fetch updatestocks for sidebar
        updatestocksApi().then(setUpdateStockRecordsForParties);
    }, []);

    useEffect(() => {
        async function fetchData() {
            const partiesRes = await parties();
            setAllParties(Array.isArray(partiesRes) ? partiesRes : []);
            const stocksData = await stocksApi();
            // Parse category field as JSON array if needed
            const cleaned = Array.isArray(stocksData)
                ? stocksData.map((s: any) => ({
                    ...s,
                    categories:
                        typeof s.categories === "string"
                            ? (() => {
                                try {
                                    const parsed = JSON.parse(s.categories);
                                    return Array.isArray(parsed) ? parsed : [parsed];
                                } catch {
                                    return [s.categories];
                                }
                            })()
                            : Array.isArray(s.categories)
                                ? s.categories
                                : [],
                })) : [];
            setAllStocks(cleaned);
            const categoriesList = getAllUniqueCategories(cleaned);
            setUniqueCategories(['All', ...categoriesList]);
            setAllAnalyticsCategories(['All', ...categoriesList]);
        }
        fetchData();
    }, []);

    function allStocksMinTransactionDate() {
        return allStocksMinTransactionDateFromStocks(allStocks);
    }
    function allStocksMaxTransactionDate() {
        return allStocksMaxTransactionDateFromStocks(allStocks);
    }

    // The balance/earning effect
    useEffect(() => {
        async function updateDashboardStats() {
            // Filter records as usual for currentBalance & earnings
            const stocksFiltered =
                (!dateRange.from && !dateRange.to)
                    ? allStocks
                    : filterStocksByDateRange(allStocks, dateRange.from, dateRange.to);

            // 1. Calculate opening balance: sum of all categories' total_amount
            // ...but only of those records whose (date_of_purchase || date) <= dateRange.from (if given), else all
            const computedOpeningBalance = calculateOpeningBalance(dateRange.from,dateRange.to,allParties);
            //console.log("computedOpeningBalance ", computedOpeningBalance); 
             setOpeningBalance(computedOpeningBalance);

            // 2. Calculate current balance from updatestocksApi current_price records for the selected date range
            const currBal = await calculateCurrentBalanceFromApi(stocksFiltered);
            setCurrentBalance(currBal);

            // 3. total_earning = current_balance - current total invested/added within current selection
            // NOTE: For earning, total invested is only from current filtered range
            const totalInvestedCurrentRange = calculateTotalInvested(stocksFiltered);
            setTotalEarning(currBal - totalInvestedCurrentRange);
        }
        updateDashboardStats();
    }, [allStocks, dateRange]);

    const totalEarningBaseValue = totalEarning;

    useEffect(() => {
        if (!useBaseCalculation) {
            setInterestSummary(null);
            return;
        }
        let pType = 'compound';
        let percent = 0;
        if (settings && settings.length > 0) {
            const t = settings.find((s: any) => s.meta_key === "interest_type");
            const p = settings.find((s: any) => s.meta_key === "percentage");
            if (t) pType = t.meta_value;
            if (p) percent = parseFloat(p.meta_value || "0");
        }
        let principal = totalEarningBaseValue;
        let startDate = interestStartDate;
        let endDate = interestEndDate;
        let minDt = allStocksMinTransactionDateFromStocks(allStocks);
        let maxDt = allStocksMaxTransactionDateFromStocks(allStocks);
        if (!startDate) {
            if (minDt) startDate = minDt.toISOString().substring(0, 10);
        }
        if (!endDate) {
            if (maxDt) endDate = maxDt.toISOString().substring(0, 10);
        }
        // Calculate time in months (month basis) for interest calculation
        let months = 1;
        if (startDate && endDate) {
            const sd = new Date(startDate);
            const ed = new Date(endDate);
            // Calculate the number of months difference
            const yearsDiff = ed.getFullYear() - sd.getFullYear();
            const monthsDiff = ed.getMonth() - sd.getMonth();
            months = yearsDiff * 12 + monthsDiff + (ed.getDate() >= sd.getDate() ? 1 : 0);
            if (months < 1) months = 1;
        }
        // Convert months to years for interest formulas
        const timeInYears = months / 12;

        let calculated = 0;
        if (pType === 'simple') {
            calculated = calculateSimpleInterest(principal, percent, timeInYears);
        } else {
            calculated = calculateCompoundInterest(principal, percent, timeInYears);
        }
        setInterestSummary({
            type: pType,
            rate: percent,
            calculated,
            start: startDate || '',
            end: endDate || '',
            time: months // reporting time in months now
        });
    }, [useBaseCalculation, interestStartDate, interestEndDate, settings, allStocks, totalEarningBaseValue]);

    useEffect(() => {
        let filteredStocks = (() => {
            if (!dateRange.from && !dateRange.to) return allStocks;
            return filterStocksByDateRange(allStocks, dateRange.from, dateRange.to);
        })();
        filteredStocks = filterStocksByCategoryAndPeriod(filteredStocks, growthCategory, growthPeriod);
        const sorted = [...filteredStocks].sort((a, b) => {
            const dateA = a.date_of_purchase || a.date || '';
            const dateB = b.date_of_purchase || b.date || '';
            const timeA = dateA ? new Date(dateA).getTime() : 0;
            const timeB = dateB ? new Date(dateB).getTime() : 0;
            return timeA - timeB;
        });
        const dateBalanceMap = new Map<string, number>();
        const dateLabelMap = new Map<string, string>();
        let opening = 0;
        sorted.forEach((stock, idx) => {
            const categories = Array.isArray(stock.categories) ? stock.categories : [];
            let stockTotal = 0;
            categories.forEach((cat: any) => {
                if (growthCategory === "All" || cat.category === growthCategory) {
                    const amt = (typeof cat.total_amount === 'string' ? parseFloat(cat.total_amount) : cat.total_amount) || 0;
                    const ic = (typeof cat.indirect_charges === 'string' ? parseFloat(cat.indirect_charges) : cat.indirect_charges) || 0;
                    stockTotal += amt + ic;
                }
            });
            const dt = stock.date_of_purchase || stock.date || new Date().toISOString().split('T')[0];
            if (dt && stockTotal !== 0) {
                const obj = new Date(dt);
                const key = obj.toISOString().split('T')[0];
                const label = obj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                if (!dateBalanceMap.has(key)) {
                    dateBalanceMap.set(key, 0);
                    dateLabelMap.set(key, label);
                }
                dateBalanceMap.set(key, (dateBalanceMap.get(key) || 0) + stockTotal);
            }
            if (idx === 0) opening = stockTotal;
        });
        const sortedDateKeys = Array.from(dateBalanceMap.keys()).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        const sortedDateLabels = sortedDateKeys.map(key => dateLabelMap.get(key) || key);
        const balanceData: number[] = [];
        let runBal = 0;
        sortedDateKeys.forEach(dateKey => {
            runBal += dateBalanceMap.get(dateKey) || 0;
            balanceData.push(runBal);
        });
        setGrowthChartData({
            labels: sortedDateLabels.length > 0 ? sortedDateLabels : sorted.map((_, i) => `Transaction ${i + 1}`),
            datasets: [{
                label: "Working Balance",
                data: balanceData.length > 0 ? balanceData : [],
                fill: false,
                backgroundColor: "#34d399",
                borderColor: "#34d399",
                borderWidth: 2,
                pointBackgroundColor: "#fff",
                pointBorderColor: "#34d399",
                tension: 0.4
            }]
        });
    }, [allStocks, growthPeriod, growthCategory, dateRange]);
    useEffect(() => {
        let filteredStocks = (() => {
            if (!dateRange.from && !dateRange.to) return allStocks;
            return filterStocksByDateRange(allStocks, dateRange.from, dateRange.to);
        })();
        filteredStocks = filterStocksByCategoryAndPeriod(filteredStocks, pieCategory, piePeriod);
        let categoryMap: { [key: string]: number } = {};
        let categoryStockBreakdown: { [key: string]: any[] } = {};
        filteredStocks.forEach((stock) => {
            const categories = Array.isArray(stock.categories) ? stock.categories : [];
            categories.forEach((cat: any) => {
                if (pieCategory === "All" || cat.category === pieCategory) {
                    const catName = cat.category || 'Other';
                    const amt = (typeof cat.total_amount === 'string' ? parseFloat(cat.total_amount) : cat.total_amount) || 0;
                    const ic = (typeof cat.indirect_charges === 'string' ? parseFloat(cat.indirect_charges) : cat.indirect_charges) || 0;
                    const sum = amt + ic;
                    if (!categoryMap[catName]) {
                        categoryMap[catName] = 0;
                        categoryStockBreakdown[catName] = [];
                    }
                    categoryMap[catName] += sum;
                    categoryStockBreakdown[catName].push({
                        stock_name: cat.stock_name,
                        unit: cat.unit,
                        total_amount: amt,
                        indirect_charges: ic,
                        perUnitCharge: typeof cat.perUnitCharge === 'string' ? parseFloat(cat.perUnitCharge) || 0 : cat.perUnitCharge || 0
                    });
                }
            });
        });
        const cEntries = Object.entries(categoryMap);
        const pieLabels = cEntries.map(([name]) => name);
        const pieValues = cEntries.map(([, amt]) => amt);
        const pieColors = ["#1294F2", "#F6830F", "#00CA4E", "#F83C3F", "#FF6B9D", "#9B59B6", "#E67E22", "#3498DB"];
        const totalCat = pieValues.reduce((s, a) => s + a, 0);
        setPieCategoryDetails(
            cEntries.map(([name, amt], i) => ({
                name,
                amount: amt,
                percentage: totalCat > 0 ? amt / totalCat * 100 : 0,
                color: pieColors[i % pieColors.length],
                stockBreakdown: categoryStockBreakdown[name]
            }))
        );
        setPieData({
            labels: pieLabels,
            datasets: [{
                data: pieValues,
                backgroundColor: pieColors.slice(0, pieLabels.length),
                borderWidth: 0,
            }]
        });
    }, [allStocks, pieCategory, piePeriod, uniqueCategories, dateRange]);

    useEffect(() => {
        function filterByCategory(stocks: Stock[], cat: string) {
            if (!cat || cat === "All") return stocks;
            return stocks.filter((stock) => {
                const cats = Array.isArray(stock.categories) ? stock.categories : [];
                return cats.some((c: any) => typeof c.category === "string" && c.category === cat);
            });
        }
        const filteredStocks = (() => {
            if (!dateRange.from && !dateRange.to) return allStocks;
            return filterStocksByDateRange(allStocks, dateRange.from, dateRange.to);
        })();
        setParty1Stocks(selectedParty1
            ? filterByCategory(filteredStocks.filter((s: Stock) => s.party_id === Number(selectedParty1)), selectedAnalyticsCategory)
            : []);
        setParty2Stocks(selectedParty2
            ? filterByCategory(filteredStocks.filter((s: Stock) => s.party_id === Number(selectedParty2)), selectedAnalyticsCategory)
            : []);
    }, [allStocks, selectedParty1, selectedParty2, selectedAnalyticsCategory, dateRange]);

    useEffect(() => {
        if (!selectedParty1 && !selectedParty2) {
            setAnalyticsData({
                labels: [],
                datasets: [
                    {
                        label: 'Party 1',
                        data: [],
                        borderColor: "#6D6AFF",
                        backgroundColor: "rgba(109,106,255,0.07)",
                        fill: true,
                        tension: 0.4
                    },
                    {
                        label: 'Party 2',
                        data: [],
                        borderColor: "#F8C615",
                        backgroundColor: "rgba(248,198,21,0.07)",
                        fill: true,
                        tension: 0.4
                    }
                ]
            });
            return;
        }
        function calcMetrics(stocks: Stock[]) {
            let totalInvestment = 0;
            const dateAmountMap: Map<string, number> = new Map();
            const dateLabelMap: Map<string, string> = new Map();
            const sortedStocks = [...stocks].sort((a, b) => {
                const dateA = a.date_of_purchase || a.date || '';
                const dateB = b.date_of_purchase || b.date || '';
                const timeA = dateA ? new Date(dateA).getTime() : 0;
                const timeB = dateB ? new Date(dateB).getTime() : 0;
                return timeA - timeB;
            });
            sortedStocks.forEach(stock => {
                const categories = Array.isArray(stock.categories) ? stock.categories : [];
                let stockTotal = 0;
                categories.forEach((cat: any) => {
                    const amt = (typeof cat.total_amount === 'string' ? parseFloat(cat.total_amount) : cat.total_amount) || 0;
                    const ic = (typeof cat.indirect_charges === 'string' ? parseFloat(cat.indirect_charges) : cat.indirect_charges) || 0;
                    if (selectedAnalyticsCategory === "All" || (cat.category && cat.category === selectedAnalyticsCategory)) {
                        stockTotal += amt + ic;
                    }
                });
                totalInvestment += stockTotal;
                const dt = stock.date_of_purchase || stock.date || new Date().toISOString().split('T')[0];
                if (dt) {
                    const obj = new Date(dt);
                    const key = obj.toISOString().split('T')[0];
                    const label = obj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    if (!dateAmountMap.has(key)) {
                        dateAmountMap.set(key, 0);
                        dateLabelMap.set(key, label);
                    }
                    dateAmountMap.set(key, (dateAmountMap.get(key) || 0) + stockTotal);
                }
            });
            const sortedDateKeys = Array.from(dateAmountMap.keys()).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
            const labels = sortedDateKeys.map(key => dateLabelMap.get(key) || key);
            const data: number[] = [];
            let cumulative = 0;
            sortedDateKeys.forEach((dateKey) => {
                cumulative += (dateAmountMap.get(dateKey) || 0);
                data.push(cumulative);
            });
            if (labels.length === 0) {
                let cumulative = 0;
                sortedStocks.forEach((stock) => {
                    const categories = Array.isArray(stock.categories) ? stock.categories : [];
                    let stockTotal = 0;
                    categories.forEach((cat: any) => {
                        const amt = (typeof cat.total_amount === 'string' ? parseFloat(cat.total_amount) : cat.total_amount) || 0;
                        const ic = (typeof cat.indirect_charges === 'string' ? parseFloat(cat.indirect_charges) : cat.indirect_charges) || 0;
                        if (
                            selectedAnalyticsCategory === "All" ||
                            (cat.category && cat.category === selectedAnalyticsCategory)
                        ) {
                            stockTotal += amt + ic;
                        }
                    });
                    cumulative += stockTotal;
                    data.push(cumulative);
                });
                const transactionLabels = sortedStocks.map((_, i) => `T${i + 1}`);
                return { totalInvestment, data, labels: transactionLabels };
            }
            return { totalInvestment, data, labels };
        }
        const party1 = allParties.find(p => p.id === Number(selectedParty1));
        const party2 = allParties.find(p => p.id === Number(selectedParty2));
        const party1Metrics = calcMetrics(party1Stocks || []);
        const party2Metrics = calcMetrics(party2Stocks || []);
        const allLabels = new Set([...(party1Metrics.labels || []), ...(party2Metrics.labels || [])]);
        const sortedLabels = Array.from(allLabels).sort((a, b) => {
            const da = new Date(a);
            const db = new Date(b);
            if (!isNaN(+da) && !isNaN(+db)) return da.getTime() - db.getTime();
            return typeof a === "string" && typeof b === "string" ? a.localeCompare(b) : 0;
        });
        const party1DataMap = new Map((party1Metrics.labels || []).map((label, idx) => [label, party1Metrics.data[idx]]));
        const party2DataMap = new Map((party2Metrics.labels || []).map((label, idx) => [label, party2Metrics.data[idx]]));
        let party1Val = 0, party2Val = 0;
        const party1Graph = sortedLabels.map(label => {
            if (party1DataMap.has(label)) party1Val = party1DataMap.get(label)!;
            return party1Val;
        });
        const party2Graph = sortedLabels.map(label => {
            if (party2DataMap.has(label)) party2Val = party2DataMap.get(label)!;
            return party2Val;
        });
        setAnalyticsData({
            labels: sortedLabels.length > 0 ? sortedLabels : ['No Data'],
            datasets: [
                {
                    label: party1?.name || 'Party 1',
                    data: party1Graph.length > 0 ? party1Graph : (selectedParty1 ? [0] : []),
                    borderColor: "#6D6AFF",
                    backgroundColor: "rgba(109,106,255,0.07)",
                    fill: true,
                    tension: 0.4
                },
                {
                    label: party2?.name || 'Party 2',
                    data: party2Graph.length > 0 ? party2Graph : (selectedParty2 ? [0] : []),
                    borderColor: "#F8C615",
                    backgroundColor: "rgba(248,198,21,0.07)",
                    fill: true,
                    tension: 0.4
                }
            ]
        })
    }, [party1Stocks, party2Stocks, selectedParty1, selectedParty2, allParties, selectedAnalyticsCategory]);

    function dashboardMinTransactionDateString() {
        const dt = allStocksMinTransactionDate();
        return dt ? dt.toISOString().substring(0, 10) : "";
    }
    function dashboardMaxTransactionDateString() {
        const dt = allStocksMaxTransactionDate();
        return dt ? dt.toISOString().substring(0, 10) : "";
    }

    return (
        <div className="bg-[#F6F6F6] min-h-screen ">
            <div className='flex flex-wrap w-full'>
                <div className="flex w-full">
                    <div
                        className={`p-6 transition-all duration-300 ${
                            sidebarCollapsed
                                ? "w-[calc(100%-64px)]"
                                : "lg:w-[75%] w-[100%]"
                        }`}
                        style={
                            sidebarCollapsed
                                ? { width: "calc(100% - 64px)", transition: "width 0.3s" }
                                : {}
                        }
                    >
                        {/* ---- DATE RANGE PICKER AT TOP ---- */}
                        <div className="flex flex-col sm:flex-row justify-start items-start sm:items-center gap-2 mb-4">
                            <div className="flex items-center gap-2">
                                <label className="text-[#3F4765] text-[15px] font-[500]">Date Range:</label>
                                <input
                                    type="date"
                                    className="border border-[#E4E8F0] px-2 py-1 rounded text-xs focus:outline-none"
                                    value={dateRange.from || ""}
                                    
                                    onChange={e =>
                                        setDateRange(prev => ({
                                            ...prev,
                                            from: e.target.value || null
                                        }))
                                    }
                                />
                                <span className="text-[#8C8C8C] text-[12px]">to</span>
                                <input
                                    type="date"
                                    className="border border-[#E4E8F0] px-2 py-1 rounded text-xs focus:outline-none"
                                    value={dateRange.to || ""}
                                    min={dateRange.from || dashboardMinTransactionDateString()}
                                    max={dashboardMaxTransactionDateString()}
                                    onChange={e =>
                                        setDateRange(prev => ({
                                            ...prev,
                                            to: e.target.value || null
                                        }))
                                    }
                                />
                                {(dateRange.from || dateRange.to) && (
                                    <button
                                        className="ml-2 text-[#3176D3] text-xs underline"
                                        onClick={() => setDateRange({ from: null, to: null })}
                                        title="Reset date range"
                                    >
                                        Reset
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center justify-between w-[100%] mb-4">
                            <div>
                                <p className="text-[#3F4765] text-[24px] font-[600]">All Parties Combined</p>
                            </div>
                            <button className="text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#1CAC70] to-[#FF9500] p-2 rounded-[8px] ">View All Details</button>
                        </div>
                        <div className='flex flex-wrap justify-between gap-[8px] w-full'>
                            {/* All Parties Combined: Opening/Current/Earnings */}
                            <div className='lg:w-[32%] md:w-[48%] w-[100%] bg-white border-[1px] border-[#DDDDDD] rounded-[14px] p-3'>
                                <div className='w-full flex justify-between items-center mb-3'>
                                    <div className='bg-[#FFF2E5] rounded-[50%] p-2'>
                                        <IoWalletSharp className='text-[#F49A40]' />
                                    </div>
                                    <HiDotsVertical />
                                </div>
                                <span className='text-[#9FA8C7] text-[14px]'>Opening Balance</span>
                                <div className='flex justify-between items-center'>
                                    <p className='text-[#25282C] text-[25px] font-[600]'>₹{openingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                    <div className='bg-[#E5FFF1] w-[75px] rounded-[40px] flex justify-center items-center py-1'>
                                        <span className='text-[#00AE4E] text-[12px]'>
                                            {openingBalance > 0 && currentBalance > 0
                                                ? `${((currentBalance - openingBalance) / openingBalance * 100).toFixed(2)}%`
                                                : '0%'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className='lg:w-[32%] md:w-[48%] w-[100%] bg-white border-[1px] border-[#DDDDDD] rounded-[14px] p-3'>
                                <div className='w-full flex justify-between items-center mb-3'>
                                    <div className='bg-[#FFDFF6] rounded-[50%] p-2'>
                                        <TbZoomMoney className='text-[#D64BC2]' />
                                    </div>
                                    <HiDotsVertical />
                                </div>
                                <span className='text-[#9FA8C7] text-[14px]'>Current Balance</span>
                                <div className='flex flex-col'>
                                    <div className='flex justify-between items-center'>
                                        <p className='text-[#25282C] text-[25px] font-[600]'>₹{currentBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                        <div className={`${totalEarning >= 0 ? 'bg-[#FFEEEE]' : 'bg-[#E5FFF1]'} w-[75px] rounded-[40px] flex justify-center items-center py-1`}>
                                            <span className={`${totalEarning >= 0 ? 'text-[#F83C3F]' : 'text-[#00AE4E]'} text-[12px]`}>
                                                {openingBalance > 0 && totalEarning !== 0
                                                    ? `${(totalEarning >= 0 ? '+' : '')}${((totalEarning / openingBalance) * 100).toFixed(2)}%`
                                                    : '0%'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='lg:w-[32%] md:w-[48%] w-[100%] bg-white border-[1px] border-[#DDDDDD] rounded-[14px] p-3'>
                                <div className='w-full flex justify-between items-center mb-3'>
                                    <div className='bg-[#DDFFE7] rounded-[50%] p-2'>
                                        <GiWallet className='text-[#27C153]' />
                                    </div>
                                    <HiDotsVertical />
                                </div>
                                <span className='text-[#9FA8C7] text-[14px]'>Total Earning</span>
                                <div className='flex justify-between items-center'>
                                    <p className='text-[#25282C] text-[25px] font-[600]'>₹{totalEarning.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                    <div className={`${totalEarning >= 0 ? 'bg-[#E5FFF1]' : 'bg-[#FFEEEE]'} w-[75px] rounded-[40px] flex justify-center items-center py-1`}>
                                        <span className={`${totalEarning >= 0 ? 'text-[#00AE4E]' : 'text-[#F83C3F]'} text-[12px]`}>
                                            {openingBalance > 0 && totalEarning !== 0
                                                ? `${(totalEarning >= 0 ? '+' : '')}${((totalEarning / openingBalance) * 100).toFixed(2)}%`
                                                : '0%'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                             {/* INTEREST BOX APPEARS AFTER TOTAL AMOUNT */}
                             {useBaseCalculation && (
                                <div className="w-full bg-gradient-to-r from-[#FFF8E6] to-[#FEFFF7] mt-4 rounded-xl p-4 border-l-8 border-[#F8C615] shadow-lg flex flex-col gap-2">
                                    {interestSummary && (
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2">
                                                <span className="inline-block bg-[#FFFBE5] text-[#C49B11] font-semibold px-3 py-1 rounded-full text-xs shadow border border-[#F8C615]">
                                                    {interestSummary.type === 'simple' ? 'Simple Interest' : 'Compound Interest'}
                                                </span>
                                                <span className="inline-block text-[#7C7128] text-xs font-medium ml-2">
                                                    <span className="bg-[#FEF3C7] px-2 py-0.5 rounded border border-[#F8C615] font-semibold mr-1">
                                                        Rate: {interestSummary.rate}%
                                                    </span>
                                                </span>
                                            </div>
                                            <div className="flex items-center ml-auto">
                                                <span className="text-[#888155] text-xs mr-2">Calculated Interest:</span>
                                                <span className="text-[#FFAA2D] text-2xl font-extrabold drop-shadow">
                                                    ₹{Number(interestSummary.calculated).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="bg-white rounded-md p-3 mt-2 flex flex-col sm:flex-row items-baseline gap-2 border border-dashed border-[#F8C615]/30">
                                        <span className="text-[#A8975D] font-medium text-xs sm:text-sm mb-1">Total Earning With Base</span>
                                        <span className="flex items-center gap-2">
                                            <span className="text-[#575A36] font-semibold text-lg">
                                                ₹{openingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                            {interestSummary && typeof interestSummary.calculated !== 'undefined' && !isNaN(Number(interestSummary.calculated)) && (
                                                <>
                                                    <span className="text-lg text-[#F8A600] font-extrabold">-</span>
                                                    <span className="text-[#FBC02D] font-semibold text-lg bg-[#FFF8E1] px-2 py-0.5 rounded">{`₹${Number(interestSummary.calculated).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                                                    <span className="text-lg text-[#F8A600] font-extrabold">=</span>
                                                    <span className="font-bold text-lg px-3 py-1 rounded-full bg-gradient-to-r from-[#FFFBE5] to-[#FEFFF7] border border-[#F8C615] text-[#F8A600] shadow-inner">
                                                        ₹{(openingBalance - Number(interestSummary.calculated)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </span>
                                                </>
                                            )}
                                        </span>
                                    </div>
                                    {/* Highlighted point  */}
                                    <div className="mt-2 flex gap-2 items-center">
                                        <span className="w-2 h-2 rounded-full bg-[#F8C615] animate-pulse shadow-lg"></span>
                                        <span className="text-[12px] sm:text-xs text-[#C49B11] font-semibold tracking-wide">
                                            Make smarter investment decisions by tracking your base interest separately!
                                        </span>
                                    </div>
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 w-full">
                                {/* Growth Card */}
                                <div className="bg-white rounded-xl p-5 shadow-sm flex flex-col gap-2 min-h-[285px]">
                                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-2">
                                        <div className="min-w-0">
                                            <div className="text-[#7B849B] font-medium text-xs mb-0.5 truncate">Growth</div>
                                            <span className="text-[#b1b5c4] text-xs font-regular truncate block">Working Balance</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2 min-w-0">
                                            <select
                                                className="rounded-[6px] py-1 px-2 text-xs bg-[#F7F9FB] text-[#B1B5C4] outline-none border border-[#E6E9F0] max-w-[120px] truncate"
                                                value={growthCategory}
                                                onChange={e => setGrowthCategory(e.target.value)}
                                            >
                                                {uniqueCategories.map((cat, i) =>
                                                    <option key={cat || i} value={cat}>{cat}</option>
                                                )}
                                            </select>
                                            <select
                                                className="rounded-[6px] py-1 px-2 text-xs bg-[#F7F9FB] text-[#B1B5C4] outline-none border border-[#E6E9F0] max-w-[120px] truncate"
                                                value={growthPeriod}
                                                onChange={e => setGrowthPeriod(e.target.value)}
                                            >
                                                {PERIOD_OPTIONS.map((p) =>
                                                    <option key={p.key} value={p.key}>{p.label}</option>
                                                )}
                                            </select>
                                        </div>
                                        <div className="min-w-0 mt-2 sm:mt-0 sm:ml-2 flex-shrink-0">
                                            <span className="text-[#B1B5C4] text-xs whitespace-nowrap">Currency</span>
                                            <span className="ml-1 text-[#222] text-xs whitespace-nowrap">Rupees</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-between gap-2 lg:flex-row">
                                        {/* Amount and chart */}
                                        <div className="flex flex-col justify-between h-[180px] min-w-0">
                                            <div className="pt-1 pb-2 flex flex-col min-w-0">
                                                <div className="text-[#24272B] font-bold text-base sm:text-xl mt-3 truncate">₹{currentBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                                <div className="text-xs text-[#B1B5C4] truncate">
                                                    {growthChartData.labels.length > 0
                                                        ? growthChartData.labels[growthChartData.labels.length - 1]
                                                        : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                </div>
                                            </div>
                                            <div className="w-full h-[110px] relative min-w-0 overflow-x-auto">
                                                {growthChartData.labels.length > 0 && growthChartData.datasets[0].data.length > 0 ? (
                                                    <>
                                                        <Line data={growthChartData} options={growthOptions} height={110} />
                                                        {growthChartData.datasets[0].data.length > 0 && (
                                                            <div className="absolute left-1/2 -translate-x-1/2 bottom-[30px] z-10 flex flex-col items-center pointer-events-none max-w-full">
                                                                <span className="bg-white shadow px-2 py-1 rounded text-xs font-semibold text-[#319F68] border border-[#e6e9f0] whitespace-nowrap">
                                                                    ₹{growthChartData.datasets[0].data[growthChartData.datasets[0].data.length - 1].toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                                                </span>
                                                                <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[10px] border-t-[#E6E9F0]"></div>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="flex items-center justify-center h-full text-[#B1B5C4] text-sm">
                                                        No transaction data available
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Pie & Data Card */}
                                <div className="bg-white rounded-xl p-5 shadow-sm flex flex-col gap-3 min-h-[285px]">
                                    <div className="flex flex-row gap-2 xs:flex-row xs:justify-between xs:items-center">
                                        <div className="font-semibold text-[#222] text-[15px] min-w-0 truncate">Asset Categories</div>
                                        <div className="flex flex-wrap gap-2 min-w-0">
                                            <select
                                                className="rounded-[6px] py-1 px-2 text-xs bg-[#F7F9FB] text-[#B1B5C4] outline-none border border-[#E6E9F0] max-w-[120px] truncate"
                                                value={pieCategory}
                                                onChange={e => setPieCategory(e.target.value)}
                                            >
                                                {uniqueCategories.map((cat, i) =>
                                                    <option key={cat || i} value={cat}>{cat}</option>
                                                )}
                                            </select>
                                            <select
                                                className="rounded-[6px] py-1 px-2 text-xs bg-[#F7F9FB] text-[#B1B5C4] outline-none border border-[#E6E9F0] max-w-[120px] truncate"
                                                value={piePeriod}
                                                onChange={e => setPiePeriod(e.target.value)}
                                            >
                                                {PERIOD_OPTIONS.map((p) =>
                                                    <option key={p.key} value={p.key}>{p.label}</option>
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2 xs:flex-row xs:gap-3 w-full">
                                        {pieData.labels.length > 0 && pieData.datasets[0].data.length > 0 ? (
                                            <>
                                                <div className="flex-shrink-0 mx-auto xs:mx-0 w-[80px] h-[80px] sm:w-[105px] sm:h-[105px]">
                                                    <Pie data={pieData} options={pieOptions} />
                                                </div>
                                                <div className="flex flex-col justify-center gap-2 text-xs sm:text-sm w-full min-w-0">
                                                    {pieCategoryDetails.map((detail, index) => (
                                                        <div key={index} className="flex flex-col xs:flex-row xs:items-center gap-1 flex-wrap xs:gap-2 min-w-0">
                                                            <div className="flex items-center gap-1">
                                                                <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: detail.color }}></span>
                                                                <span className="font-semibold whitespace-nowrap">{detail.percentage.toFixed(1)}%</span>
                                                                <span className="text-[#B1B5C4] truncate">{detail.name}</span>
                                                                <span className={`${detail.amount >= 0 ? 'text-[#319F68]' : 'text-[#D44334]'} font-bold ml-1 xs:ml-2 whitespace-nowrap truncate`}>
                                                                    ₹{detail.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                </span>
                                                            </div>
                                                            {detail.stockBreakdown && detail.stockBreakdown.length > 0 && (
  <div className="text-[10px] text-[#69813C] pl-5 max-h-16 overflow-y-auto">
    {detail.stockBreakdown
      .filter((s: any) => !!s.stock_name)
      .map((s: any, idx: number) => (
        <div key={idx} className="truncate">
          • {s.stock_name}
          {(s.unit ? <span> ({s.unit})</span> : '')}
          {typeof s.total_amount === 'number' && (
            <span> ₹{s.total_amount.toLocaleString('en-IN')}</span>
          )}
        </div>
      ))}
  </div>
)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex items-center justify-center w-full h-[150px] text-[#B1B5C4] text-sm">
                                                No stock categories available
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* Analytics section */}
                            <div className="bg-white rounded-xl p-5 shadow-sm mb-4 w-full">
                                <div className="flex flex-col gap-4 mb-4">
                                    <div className="font-semibold text-[#222] text-base sm:text-lg truncate">Analytics - Party Comparison</div>
                                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                                        <div className="flex flex-col gap-2 flex-1 min-w-0 w-full sm:w-auto">
                                            <label className="text-xs text-[#7B849B]">Select Party 1</label>
                                            <select
                                                value={selectedParty1}
                                                onChange={(e) => setSelectedParty1(e.target.value)}
                                                className="rounded-[6px] py-2 px-3 text-sm bg-[#F7F9FB] text-[#222] outline-none border border-[#E6E9F0] w-full"
                                            >
                                                <option value="">-- Select Party --</option>
                                                {allParties.map((p) => (
                                                    <option key={p.id} value={p.id.toString()}>
                                                        {p.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="flex flex-col gap-2 flex-1 min-w-0 w-full sm:w-auto">
                                            <label className="text-xs text-[#7B849B]">Select Party 2</label>
                                            <select
                                                value={selectedParty2}
                                                onChange={(e) => setSelectedParty2(e.target.value)}
                                                className="rounded-[6px] py-2 px-3 text-sm bg-[#F7F9FB] text-[#222] outline-none border border-[#E6E9F0] w-full"
                                            >
                                                <option value="">-- Select Party --</option>
                                                {allParties.map((p) => (
                                                    <option key={p.id} value={p.id.toString()}>
                                                        {p.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Analytics category filter */}
                                        <div className="flex flex-col gap-2 flex-1 min-w-0 w-full sm:w-auto mt-2 sm:mt-0">
                                            <label className="text-xs text-[#7B849B]">Select Category</label>
                                            <select
                                                value={selectedAnalyticsCategory}
                                                onChange={e => setSelectedAnalyticsCategory(e.target.value)}
                                                className="rounded-[6px] py-2 px-3 text-sm bg-[#F7F9FB] text-[#222] outline-none border border-[#E6E9F0] w-full"
                                            >
                                                {allAnalyticsCategories.map((cat, i) =>
                                                    <option key={cat || i} value={cat}>{cat}</option>
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                    {(selectedParty1 || selectedParty2) && (
                                        <>
                                            <div className="flex gap-2 items-center flex-wrap min-w-0">
                                                {selectedParty1 && (
                                                    <div className="flex items-center gap-1 xs:gap-2 min-w-0">
                                                        <span className="inline-block w-3 h-3 rounded-full bg-[#6D6AFF] flex-shrink-0"></span>
                                                        <span className="text-xs text-[#7B849B] truncate">
                                                            {allParties.find(p => p.id === Number(selectedParty1))?.name || 'Party 1'}
                                                        </span>
                                                    </div>
                                                )}
                                                {selectedParty2 && (
                                                    <div className="flex items-center gap-1 xs:gap-2 min-w-0 ml-0 xs:ml-4">
                                                        <span className="inline-block w-3 h-3 rounded-full bg-[#F8C615] flex-shrink-0"></span>
                                                        <span className="text-xs text-[#7B849B] truncate">
                                                            {allParties.find(p => p.id === Number(selectedParty2))?.name || 'Party 2'}
                                                        </span>
                                                    </div>
                                                )}
                                                {selectedAnalyticsCategory && selectedAnalyticsCategory !== "All" && (
                                                    <div className="flex items-center gap-1 xs:gap-2 min-w-0 ml-0 xs:ml-4">
                                                        <span className="inline-block w-3 h-3 rounded-full bg-[#27C153] flex-shrink-0"></span>
                                                        <span className="text-xs text-[#7B849B] truncate">
                                                            {selectedAnalyticsCategory}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            {selectedParty1 && selectedParty2 && (
                                                <PartyComparisonSummary
                                                    party1Stocks={party1Stocks}
                                                    party2Stocks={party2Stocks}
                                                    party1Name={allParties.find(p => p.id === Number(selectedParty1))?.name || 'Party 1'}
                                                    party2Name={allParties.find(p => p.id === Number(selectedParty2))?.name || 'Party 2'}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="h-[160px] sm:h-[180px] mt-2 overflow-x-auto">
                                    {analyticsData.labels.length > 0 && analyticsData.datasets[0].data.length > 0 ? (
                                        <Line
                                            data={analyticsData}
                                            options={{
                                                ...analyticsOptions,
                                                plugins: {
                                                    ...analyticsOptions.plugins,
                                                    legend: {
                                                        ...analyticsOptions.plugins?.legend,
                                                        position: 'top' as const,
                                                    },
                                                },
                                            }}
                                            height={160}
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center h-full text-[#B1B5C4] text-sm">
                                            No analytics data available
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Sticky Sidebar - compact/cleaner, less top space, improved visuals */}
                    <div
                        className={`bg-white h-screen sticky top-0 bottom-0 shadow transition-all duration-300 ${
                            sidebarCollapsed ? "lg:w-16 w-16" : "lg:w-1/4 w-full"
                        }`}
                    >
                        {/* Sticky inner sidebar with less gap from top */}
                        <div className="sticky top-2">
                            {/* Collapse/Expand Button */}
                            <div className="flex items-center justify-end pt-2 pb-0 px-3">
                                <button
                                    className="p-2 rounded hover:bg-[#f0f0ff] transition"
                                    onClick={() => setSidebarCollapsed((prev: boolean) => !prev)}
                                    aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                                    type="button"
                                >
                                    {sidebarCollapsed ? (
                                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                                            <path d="M10 6l6 6-6 6" stroke="#3F4765" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                                            <path d="M14 6l-6 6 6 6" stroke="#3F4765" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {/* Calculation base block */}
                            <div className={`bg-[#F1FFF6] rounded-lg shadow-sm mx-3 mt-3 p-3 transition-all duration-300 ${sidebarCollapsed ? "px-2 py-2" : ""}`}>
                                <div className={`${sidebarCollapsed ? "opacity-0 h-0 m-0 overflow-hidden" : "mb-2"} text-xs text-[#3F4765] font-semibold`}>
                                    Calculation Mode
                                </div>
                                <div className={`flex rounded p-2 items-center justify-center bg-[#e8f8ef] ${sidebarCollapsed ? "flex-col gap-1 p-0" : "flex-row gap-4"}`}>
                                    <label className={`flex items-center gap-2 cursor-pointer ${sidebarCollapsed ? "flex-col gap-0" : ""}`}>
                                        <input
                                            type="radio"
                                            name="basecalculation"
                                            value="yes"
                                            checked={useBaseCalculation}
                                            onChange={() => setUseBaseCalculation(true)}
                                            className="accent-[#27C153]"
                                        />
                                        {!sidebarCollapsed && (
                                            <span className="text-xs font-medium text-[#254822]">Yes</span>
                                        )}
                                    </label>
                                    <label className={`flex items-center gap-2 cursor-pointer ${sidebarCollapsed ? "flex-col gap-0" : ""}`}>
                                        <input
                                            type="radio"
                                            name="basecalculation"
                                            value="no"
                                            checked={!useBaseCalculation}
                                            onChange={() => setUseBaseCalculation(false)}
                                            className="accent-[#F83C3F]"
                                        />
                                        {!sidebarCollapsed && (
                                            <span className="text-xs font-medium text-[#872727]">No</span>
                                        )}
                                    </label>
                                </div>
                            </div>

                            {/* Parties title & dropdown */}
                            {!sidebarCollapsed && (
                                <div className="flex items-center justify-between mx-3 mt-3 mb-1">
                                    <span className='text-[#3F4765] text-base font-semibold'>Parties</span>
                                    <select name="" id="" className='text-[#969CCB] text-xs bg-transparent focus:outline-none'>
                                        <option value="All">All</option>
                                    </select>
                                </div>
                            )}

                            {/* Main party list */}
                            <div className="overflow-x-auto w-full mt-2">
                                <table className={`mr-2 ml-2 border-separate border-spacing-y-2 transition-all duration-300 w-full ${sidebarCollapsed ? "text-center" : ""}`}>
                                    <tbody>
                                        {allParties.map((party: Party) => {
                                            const { invested, returned, percent } = getPartyInvestmentAndReturn(
                                                party, allStocks, updateStockRecordsForParties
                                            );
                                            return (
                                                <tr key={party.id} className="bg-white group hover:bg-[#f7fafb] transition rounded-lg">
                                                    <td className="pl-2 pr-1 py-2 align-middle">
                                                        <Link to={`/party/${party.id}`} className="flex items-center gap-1">
                                                            <span className="text-xs font-bold text-white bg-[#FF8933] flex items-center justify-center h-6 w-6 rounded-full shadow">
                                                                {party.name && typeof party.name === 'string' ? party.name.charAt(0) : ''}
                                                            </span>
                                                            {!sidebarCollapsed && (
                                                                <span className="ml-1 text-[14px] font-medium text-[#3F4765] whitespace-nowrap truncate max-w-[110px]">
                                                                    {party.name}
                                                                </span>
                                                            )}
                                                        </Link>
                                                    </td>
                                                    {!sidebarCollapsed && (
                                                        <>
                                                            <td className="py-2 px-2 align-middle">
                                                                <div className="flex flex-col">
                                                                    <span title="Invested" className="font-semibold text-[#3F4765] text-xs leading-tight">
                                                                        ₹{invested.toLocaleString('en-IN')}
                                                                    </span>
                                                                    <span className="text-[#7B849B] text-[11px] mt-[1px]" title="Current Value">
                                                                        ₹{returned.toLocaleString('en-IN')}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="py-2 px-2 align-middle">
                                                                <span className={`font-bold text-sm ${percent >= 0 ? "text-[#00AE4E]" : "text-[#F83C3F]"}`}>
                                                                    {percent > 0 ? "+" : ""}
                                                                    {percent.toFixed(2)}%
                                                                </span>
                                                            </td>
                                                        </>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {/* BalanceSheet button at the bottom */}
                                {!sidebarCollapsed && (
                                    <div className="flex justify-end mt-5 mr-4">
                                        <Link
                                            to="/BalanceSheet"
                                            className="inline-block bg-gradient-to-r from-[#6742c7] to-[#3C1E77] text-white px-4 py-2 rounded-lg shadow hover:from-[#5531a8] hover:to-[#2A1056] text-xs font-medium tracking-wide transition"
                                            style={{ textDecoration: "none" }}
                                        >
                                            Balance Sheet
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
